import { useMutation, UseMutationResult } from "@tanstack/react-query";
import {
  useCallback,
  useState,
  createContext,
  useContext,
  ReactNode,
  useMemo
} from "react";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { CombinedIncomeSubStep } from "../../constants";
import {
  CombinedIncomeStepData,
  CreateIncomePayload,
  DocumentType,
  NavigationData
} from "./types";
import { combinedIncomeScreenFlows } from "./constants";
import { shouldSkipSupervisorDetails } from "./utils";
import {
  type Application,
  type Applicant,
  type Verification,
  type CombinedIncomeVerificationSchema,
  FieldDisplaySetting,
  IncomeType
} from "shared/types";
import { AxiosResponse } from "axios";
import { getErrorMessage } from "utils/error";
import { getByPath } from "shared/utils";

interface CombinedIncomeContextValue {
  application: Application;
  applicant: Applicant;
  verification?: Verification;
  stepData: CombinedIncomeStepData;
  verificationSchema?: CombinedIncomeVerificationSchema;
  isFieldRequired: (path: string[]) => boolean;
  isFieldVisible: (path: string[]) => boolean;
  skipSupervisorDetails: boolean;
  skipHousingAssistance: boolean;
  finishVerification: () => void;
  isFinishingVerification: boolean;
  updateStepMutation: UseMutationResult<
    AxiosResponse<CombinedIncomeStepData>,
    Error,
    Partial<CombinedIncomeStepData>,
    unknown
  >;
  createIncomeMutation: UseMutationResult<
    { id: number },
    Error,
    CreateIncomePayload,
    unknown
  >;
  refetchVerification: () => void;
  refetchApplication: () => void;
  onNextStep: () => void;
  getNextStep: (
    currentStep: CombinedIncomeSubStep,
    data?: NavigationData
  ) => CombinedIncomeSubStep | null;
  getBackStep: (
    currentStep: CombinedIncomeSubStep,
    data?: NavigationData
  ) => CombinedIncomeSubStep | null;
}

const CombinedIncomeContext = createContext<
  CombinedIncomeContextValue | undefined
>(undefined);

interface CombinedIncomeProviderProps {
  applicant: Applicant;
  application: Application;
  verification?: Verification;
  refetchApplication: () => void;
  refetchVerification: () => void;
  onNextStep: () => void;
  children: ReactNode;
}

export const CombinedIncomeProvider = ({
  applicant,
  application,
  verification,
  refetchApplication,
  refetchVerification,
  onNextStep,
  children
}: CombinedIncomeProviderProps) => {
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentType | null>(null);

  const combinedIncomeStep = useMemo(
    () => verification?.steps?.find((step) => step.name === "combined_income"),
    [verification?.steps]
  );

  const stepData: CombinedIncomeStepData = (combinedIncomeStep?.data ??
    {}) as CombinedIncomeStepData;

  const verificationSchema = verification?.settings as
    | CombinedIncomeVerificationSchema
    | undefined;

  const { isFieldRequired, isFieldVisible } = useMemo(() => {
    const getVerificationFieldSetting = (path: string[]) =>
      getByPath(verificationSchema, [
        stepData.currentIncomeType || IncomeType.Employment,
        "fields",
        ...path
      ]) as FieldDisplaySetting | undefined;

    return {
      isFieldRequired: (path: string[]) =>
        getVerificationFieldSetting(path) === FieldDisplaySetting.Required,
      isFieldVisible: (path: string[]) =>
        getVerificationFieldSetting(path) !== FieldDisplaySetting.Hidden
    };
  }, [verificationSchema, stepData.currentIncomeType]);

  const skipSupervisorDetails = useMemo(
    () =>
      shouldSkipSupervisorDetails(
        verificationSchema,
        stepData.currentIncomeType
      ),
    [verificationSchema, stepData.currentIncomeType]
  );

  const skipHousingAssistance =
    verificationSchema?.housing_assistance_program_enabled === false;

  const updateStepMutation = useMutation({
    mutationKey: [
      "UPDATE_COMBINED_INCOME_STEP",
      application.id,
      verification?.id
    ],
    mutationFn: (data: Partial<CombinedIncomeStepData>) =>
      instance.patch(
        `/portal/v1/applications/${application.id}/verifications/${verification?.id}/steps/combined_income?applicant_id=${applicant.id}`,
        { data }
      ),
    onSuccess: () => {
      refetchVerification();
    }
  });

  const createIncomeMutation = useMutation({
    mutationKey: ["CREATE_INCOME", application.id, verification?.id],
    mutationFn: (data: CreateIncomePayload) =>
      instance
        .post(
          `/portal/v1/applications/${application.id}/verifications/${verification?.id}/income?applicant_id=${applicant.id}`,
          data
        )
        .then((res) => res.data),
    onSuccess: () => {
      refetchVerification();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to create income");
    }
  });

  const finishVerificationMutation = useMutation({
    mutationFn: async () => {
      await instance.post(
        `/portal/v1/applications/${application.id}/verifications/${verification?.id}/finish?applicant_id=${applicant.id}`
      );
    },
    onSuccess: () => {
      updateStepMutation.mutate(
        { lastStep: CombinedIncomeSubStep.Completed },
        {
          onSuccess: () => {
            refetchVerification();
            onNextStep();
          },
          onError: (error) => {
            toast.error(
              getErrorMessage(error) || "Failed to complete combined income"
            );
          }
        }
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to complete verification");
    }
  });

  const finishVerification = useCallback(
    () => finishVerificationMutation.mutate(),
    [finishVerificationMutation]
  );
  const isFinishingVerification = finishVerificationMutation.isPending;

  const getNextStep = useCallback(
    (
      currentStep: CombinedIncomeSubStep,
      data?: NavigationData
    ): CombinedIncomeSubStep | null => {
      const flowConfig = combinedIncomeScreenFlows[currentStep];

      if (!flowConfig.next) return null;

      if (typeof flowConfig.next === "string") return flowConfig.next;

      if (Array.isArray(flowConfig.next))
        for (const condition of flowConfig.next)
          if (condition.condition(data)) return condition.target;

      return null;
    },
    []
  );

  const getBackStep = useCallback(
    (
      currentStep: CombinedIncomeSubStep,
      data?: NavigationData
    ): CombinedIncomeSubStep | null => {
      const flowConfig = combinedIncomeScreenFlows[currentStep];

      if (!flowConfig.back) return null;

      if (typeof flowConfig.back === "string") return flowConfig.back;

      if (Array.isArray(flowConfig.back))
        for (const condition of flowConfig.back)
          if (condition.condition(data)) return condition.target;

      return null;
    },
    []
  );

  const value: CombinedIncomeContextValue = useMemo(
    () => ({
      application,
      verification,
      applicant,
      stepData,
      verificationSchema,
      isFieldRequired,
      isFieldVisible,
      skipSupervisorDetails,
      skipHousingAssistance,
      finishVerification,
      isFinishingVerification,
      updateStepMutation,
      createIncomeMutation,
      refetchVerification,
      refetchApplication,
      onNextStep,
      selectedDocumentType,
      setSelectedDocumentType,
      getNextStep,
      getBackStep
    }),
    [
      application,
      verification,
      applicant,
      stepData,
      verificationSchema,
      isFieldRequired,
      isFieldVisible,
      skipSupervisorDetails,
      skipHousingAssistance,
      finishVerification,
      finishVerificationMutation,
      updateStepMutation,
      createIncomeMutation,
      refetchVerification,
      refetchApplication,
      onNextStep,
      selectedDocumentType,
      getNextStep,
      getBackStep
    ]
  );

  return (
    <CombinedIncomeContext.Provider value={value}>
      {children}
    </CombinedIncomeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCombinedIncome = () => {
  const context = useContext(CombinedIncomeContext);
  if (!context)
    throw new Error(
      "useCombinedIncome must be used within CombinedIncomeProvider"
    );
  return context;
};
